"use client";

import { Event, EventService, ReminderService } from "@/app/service";
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import EmptyState from "../ui/EmptyState";
import SkeletonLoader from "../ui/SkeletonLoader";
import { useToast } from "../ui/Toast";
import EmailReminderModal from "../modals/EmailReminderModal";

interface EventListProps {
    userType: "viewers" | "commentators";
}

function EventList({ userType }: EventListProps) {
    const [events, setEvents] = useState<Event[]>([]);
    const [error, setError] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [reminderLoading, setReminderLoading] = useState<{ [key: string]: boolean }>({});
    const [userReminders, setUserReminders] = useState<string[]>([]);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [selectedEventForReminder, setSelectedEventForReminder] = useState<Event | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [screenSize, setScreenSize] = useState<'mobile' | 'tablet' | 'desktop' | 'xl'>('xl');
    const router = useRouter();
    const { user } = useAuthenticator((context) => [context.user]);
    const { addToast } = useToast();

    // Detect screen size for responsive pagination
    useEffect(() => {
        const updateScreenSize = () => {
            const width = window.innerWidth;
            if (width < 768) {
                setScreenSize('mobile');
            } else if (width < 1024) {
                setScreenSize('tablet');
            } else if (width < 1280) {
                setScreenSize('desktop');
            } else {
                setScreenSize('xl');
            }
        };

        // Set initial screen size
        updateScreenSize();

        // Add event listener for window resize
        window.addEventListener('resize', updateScreenSize);

        // Cleanup
        return () => window.removeEventListener('resize', updateScreenSize);
    }, []);

    // Calculate events per page based on actual screen size - max 2 rows
    const getEventsPerPage = () => {
        switch (screenSize) {
            case 'mobile': return 2;  // 1 column × 2 rows = 2 events
            case 'tablet': return 4;  // 2 columns × 2 rows = 4 events
            case 'desktop': return 6; // 3 columns × 2 rows = 6 events
            case 'xl': return 8;      // 4 columns × 2 rows = 8 events
            default: return 8;
        }
    };

    // Group events by sport category
    const footballEvents = events.filter(event => event.sportCategory === 'FOOTBALL');
    const cyclingEvents = events.filter(event => event.sportCategory === 'CYCLING');
    const otherEvents = events.filter(event => event.sportCategory !== 'FOOTBALL' && event.sportCategory !== 'CYCLING');


    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const events = await EventService.getEvents()

                // Sort events by sport category (FOOTBALL first), then status priority (LIVE, SCHEDULED, FINISHED), then by scheduled start date
                const sortedEvents = events.sort((a, b) => {
                    // Define sport category priority: FOOTBALL = 0, CYCLING = 1, others = 2
                    const getSportPriority = (sportCategory: string) => {
                        switch (sportCategory) {
                            case "FOOTBALL": return 0;
                            case "CYCLING": return 1;
                            default: return 2;
                        }
                    };

                    const sportPriorityA = getSportPriority(a.sportCategory || '');
                    const sportPriorityB = getSportPriority(b.sportCategory || '');

                    // First sort by sport category
                    if (sportPriorityA !== sportPriorityB) {
                        return sportPriorityA - sportPriorityB;
                    }

                    // Define status priority: LIVE = 0, SCHEDULED = 1, FINISHED = 2
                    const getStatusPriority = (status: string) => {
                        switch (status) {
                            case "LIVE": return 0;
                            case "SCHEDULED": return 1;
                            case "FINISHED": return 2;
                            default: return 3;
                        }
                    };

                    const statusPriorityA = getStatusPriority(a.status);
                    const statusPriorityB = getStatusPriority(b.status);

                    // Within same sport category, sort by status priority
                    if (statusPriorityA !== statusPriorityB) {
                        return statusPriorityA - statusPriorityB;
                    }

                    // Within same status, sort by scheduled start date
                    if (!a.scheduledStartDatetime && !b.scheduledStartDatetime) return 0;
                    if (!a.scheduledStartDatetime) return 1; // Events without dates go to end
                    if (!b.scheduledStartDatetime) return -1;

                    return new Date(a.scheduledStartDatetime).getTime() - new Date(b.scheduledStartDatetime).getTime();
                });

                setEvents(sortedEvents);

                // Fetch user reminders if user is authenticated
                if (user?.signInDetails?.loginId) {
                    try {
                        const reminders = await ReminderService.getUserReminders(user.signInDetails.loginId);
                        setUserReminders(reminders.eventKeys);
                    } catch (err) {
                        console.error("Error fetching user reminders:", err);
                    }
                }
            } catch (err) {
                setError("Failed to fetch events");
                console.error("Error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchEvents().catch((error) => {
            console.error("Error in fetchEvents useEffect:", error);
            setError("Failed to fetch events");
        });
    }, [user]);


    const handleSetReminder = async (eventKey: string, emailAddress?: string) => {
        const event = events.find(e => e.eventKey === eventKey);
        if (!event) return;

        // If user is not logged in and no email provided, show modal
        if (!user?.signInDetails?.loginId && !emailAddress) {
            setSelectedEventForReminder(event);
            setEmailModalOpen(true);
            return;
        }

        const email = emailAddress || user?.signInDetails?.loginId;
        if (!email) return;

        setReminderLoading(prev => ({ ...prev, [eventKey]: true }));

        try {
            const isReminderSet = userReminders.includes(eventKey);

            if (isReminderSet) {
                await ReminderService.removeReminder(eventKey, email);
                setUserReminders(prev => prev.filter(key => key !== eventKey));

                addToast({
                    type: 'success',
                    title: 'Reminder Removed',
                    message: `You will no longer receive a reminder for "${event.description}"`,
                    duration: 5000
                });
            } else {
                await ReminderService.setReminder(eventKey, email);
                setUserReminders(prev => [...prev, eventKey]);

                const eventDate = event.scheduledStartDatetime
                    ? new Date(event.scheduledStartDatetime).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    })
                    : 'when it starts';

                addToast({
                    type: 'success',
                    title: 'Reminder Set Successfully!',
                    message: `We'll email you about "${event.description}" on ${eventDate}`,
                    duration: 6000
                });
            }
        } catch (err) {
            console.error("Error setting reminder:", err);
            addToast({
                type: 'error',
                title: 'Failed to Set Reminder',
                message: 'Please try again later or contact support if the problem persists',
                duration: 5000
            });
        } finally {
            setReminderLoading(prev => ({ ...prev, [eventKey]: false }));
            setEmailModalOpen(false);
            setSelectedEventForReminder(null);
        }
    };

    if (loading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <SkeletonLoader type="eventCard" count={8} />
            </div>
        );
    }

    if (error) {
        return (
            <EmptyState
                type="events"
                title="Failed to Load Events"
                description="We couldn't load the events at this time. Please check your connection and try again."
                actionText="Retry"
                onAction={() => window.location.reload()}
            />
        );
    }

    if (events.length === 0) {
        return (
            <EmptyState
                type="events"
                title={userType === "commentators" ? "No Events Available to Commentate" : "No Live Events"}
                description={
                    userType === "commentators"
                        ? "There are currently no events available for commentary. Check back later for new opportunities."
                        : "There are currently no live events to watch. New events will appear here when they become available."
                }
                actionText="Refresh"
                onAction={() => window.location.reload()}
            />
        );
    }

    // Fixed grid layout for 2 rows maximum
    const getGridLayout = () => {
        return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6";
    };

    const getEventCardSize = () => {
        return "h-[160px] w-full";
    };

    const isEventClickable = (status: string) => {
        return status === "LIVE" || status === "FINISHED" || status === "SCHEDULED";
    };

    const getEventCardStyles = (status: string) => {
        const baseStyles = "overflow-hidden rounded-xl shadow-lg transition-all duration-300 group bg-white";

        if (status === "LIVE" || status === "FINISHED" || status === "SCHEDULED") {
            return `${baseStyles} cursor-pointer hover:shadow-xl transform hover:scale-105`;
        } else {
            return `${baseStyles}`;
        }
    };

    const getStatusIndicator = (status: string) => {
        switch (status) {
            case "LIVE":
                return (
                    <div className="absolute top-3 right-3 flex items-center gap-1 bg-widertalk-error text-white px-2 py-1 rounded-full text-xs font-bold">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        <span className="uppercase">Live</span>
                    </div>
                );
            case "SCHEDULED":
                return (
                    <div className="absolute top-3 right-3 flex items-center gap-1 bg-widertalk-navy text-white px-2 py-1 rounded-full text-xs font-bold">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        <span className="uppercase">Scheduled</span>
                    </div>
                );
            case "FINISHED":
                return (
                    <div className="absolute top-3 right-3 flex items-center gap-1 bg-gray-600 text-white px-2 py-1 rounded-full text-xs font-bold">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="uppercase">Finished</span>
                    </div>
                );
            default:
                return null;
        }
    };

    const renderEventCard = (event: Event, i: number) => (
        <div
            key={i}
            className={getEventCardStyles(event.status)}
        >
            {/* Image container */}
            <div
                className={`relative overflow-hidden rounded-t-xl w-full ${getEventCardSize()}`}
                onClick={() => {
                    if (isEventClickable(event.status)) {
                        if (event.status === "FINISHED") {
                            router.push(`/playback`);
                        } else {
                            router.push(
                                `/${userType === "commentators" ? "commentate" : "watch"}/${event.eventKey}`,
                            );
                        }
                    }
                }}
            >
                {/* Status indicator */}
                {getStatusIndicator(event.status)}

                {/* Event image */}
                <img
                    src={event.imageUrl || "/dummy-background.png"}
                    alt={event.description}
                    className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-300 ${event.status === "SCHEDULED" || event.status === "FINISHED" ? "opacity-50" : ""
                        }`}
                />
            </div>

            {/* Event details below image */}
            <div className="p-3 bg-white rounded-b-xl">
                <h3 className="font-bold mb-1 text-base text-widertalk-navy group-hover:text-widertalk-navy/80 transition-colors">
                    {event.description}
                </h3>

                {event.scheduledStartDatetime && (
                    <p className="text-gray-500 text-xs mb-2">
                        {new Date(event.scheduledStartDatetime).toLocaleString()}
                    </p>
                )}

                <div className="flex items-center justify-between">
                    {/* Set Reminder Button - only show for scheduled events */}
                    {event.status === "SCHEDULED" && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSetReminder(event.eventKey);
                            }}
                            disabled={reminderLoading[event.eventKey]}
                            className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors flex items-center gap-1 ${
                                userReminders.includes(event.eventKey)
                                    ? "bg-widertalk-navy text-white hover:bg-widertalk-navy/90"
                                    : "bg-widertalk-lime text-widertalk-navy hover:bg-widertalk-lime/90"
                            } ${reminderLoading[event.eventKey] ? "cursor-not-allowed" : ""}`}
                        >
                            {reminderLoading[event.eventKey] ? (
                                <>
                                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>...</span>
                                </>
                            ) : userReminders.includes(event.eventKey) ? (
                                <>
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span>Reminder Set</span>
                                </>
                            ) : (
                                <>
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                    </svg>
                                    <span>Set Reminder</span>
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            {/* Football Events Section */}
            {footballEvents.length > 0 && (
                <div className="mb-12">
                    <h2 className="text-2xl font-bold text-widertalk-navy mb-6">Football</h2>
                    <div className={getGridLayout()}>
                        {footballEvents.map((event, i) => renderEventCard(event, i))}
                    </div>
                </div>
            )}

            {/* Cycling Events Section */}
            {cyclingEvents.length > 0 && (
                <div className="mb-12">
                    <h2 className="text-2xl font-bold text-widertalk-navy mb-6">Cycling</h2>
                    <div className={getGridLayout()}>
                        {cyclingEvents.map((event, i) => renderEventCard(event, i))}
                    </div>
                </div>
            )}

            {/* Other Events Section */}
            {otherEvents.length > 0 && (
                <div className="mb-12">
                    <h2 className="text-2xl font-bold text-widertalk-navy mb-6">Other Events</h2>
                    <div className={getGridLayout()}>
                        {otherEvents.map((event, i) => renderEventCard(event, i))}
                    </div>
                </div>
            )}

            {/* Email Reminder Modal */}
            <EmailReminderModal
                isOpen={emailModalOpen}
                onClose={() => {
                    setEmailModalOpen(false);
                    setSelectedEventForReminder(null);
                }}
                onSubmit={(email) => {
                    if (selectedEventForReminder) {
                        handleSetReminder(selectedEventForReminder.eventKey, email);
                    }
                }}
                eventTitle={selectedEventForReminder?.description || ''}
                loading={selectedEventForReminder ? reminderLoading[selectedEventForReminder.eventKey] : false}
            />
        </>
    );
}

export default EventList;
